/* OnboardingFileMenuGlow.jsx — Feb-2026 guided-mode hand-hold.
 *
 * Shows a small focused popup after the user selects "Guided" mode
 * on a blank project, then glows the File menu trigger, then (on open)
 * glows the "Load Manuscript" item so new users always know step 1.
 *
 * State machine (stored in localStorage to survive a reload):
 *   'popup'           — show the instruction modal
 *   'file-menu'       — modal dismissed, File menu button pulses amber
 *   'load-manuscript' — File menu opened once, Load Manuscript item pulses
 *   'done'            — user clicked Load Manuscript (or dismissed manually)
 *
 * Usage in AppShell:
 *   const { glowStep, advance, dismiss } = useOnboardingGlow(showImportHint);
 *   <OnboardingPopup open={glowStep === 'popup'} onClose={() => advance()} />
 *   <MenuBar glowFileMenu={glowStep === 'file-menu' || glowStep === 'load-manuscript'} ... />
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Folder, ArrowRight, X } from 'lucide-react';

/* ── Persistence key ─────────────────────────────────────────────── */
const KEY_STEP  = 'loom:onboarding-glow-step-v1';

function readStep() {
  try   { return localStorage.getItem(KEY_STEP) || null; }
  catch { return null; }
}
function saveStep(s) {
  try   { if (s === 'done') localStorage.setItem(KEY_STEP, 'done');
          else               localStorage.setItem(KEY_STEP, s); }
  catch { /* blocked */ }
}

/* ── Hook: manage the step state ────────────────────────────────── */
export function useOnboardingGlow(active) {
  const [step, setStep] = useState(() => readStep());

  // When the feature first becomes active (blank project, guided mode)
  // and the user hasn't progressed beyond 'done', start at 'popup'.
  useEffect(() => {
    if (!active) return;
    const stored = readStep();
    if (!stored) { saveStep('popup'); setStep('popup'); }
    else          { setStep(stored); }
  }, [active]);

  const advance = useCallback((to) => {
    // If explicit target supplied, jump there; otherwise follow the chain.
    const next = to || { popup: 'file-menu', 'file-menu': 'load-manuscript', 'load-manuscript': 'done' }[step] || 'done';
    saveStep(next);
    setStep(next);
  }, [step]);

  const dismiss = useCallback(() => { saveStep('done'); setStep('done'); }, []);

  return { glowStep: step, advance, dismiss };
}

/* ── Glow CSS classes ───────────────────────────────────────────── */
// Exported so MenuBar / FileMenu can import them without prop-drilling.
export const GLOW_TRIGGER_CLASS =
  'ring-2 ring-amber-400 ring-offset-1 ring-offset-background ' +
  'shadow-[0_0_10px_2px_rgba(251,191,36,0.45)] animate-pulse';

export const GLOW_ITEM_CLASS =
  '!bg-amber-50 !text-amber-900 font-semibold dark:!bg-amber-900/20 dark:!text-amber-200';

/* ── Popup modal ─────────────────────────────────────────────────── */
export default function OnboardingFileMenuPopup({ open, onClose, onShowLoadManuscript }) {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Card — anchored top-left so the arrow lines up with the File menu */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Getting started hint"
        data-testid="onboarding-file-menu-popup"
        className="fixed z-[201] left-4 top-14 w-72 rounded-xl border border-amber-300/70
                   bg-white shadow-2xl shadow-amber-200/40
                   dark:bg-[#1e1a14] dark:border-amber-700/50"
      >
        {/* Amber top stripe */}
        <div className="h-1.5 w-full rounded-t-xl bg-gradient-to-r from-amber-400 to-amber-500" />

        <div className="px-5 pt-4 pb-5">
          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 rounded p-1 text-muted-foreground hover:bg-muted transition-colors"
            aria-label="Dismiss"
            data-testid="onboarding-popup-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>

          {/* Icon */}
          <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <Folder className="h-5 w-5 text-amber-600" />
          </div>

          <h3 className="text-[14px] font-semibold text-foreground leading-snug">
            Begin a manuscript by typing into the writing pane.
          </h3>
          <p className="mt-1.5 text-[12px] text-muted-foreground leading-relaxed">
            Just start writing in the editor on the right — your work is
            saved automatically as you go.
          </p>

          {/* Primary CTA — just dismiss and start writing */}
          <button
            type="button"
            onClick={onClose}
            data-testid="onboarding-popup-got-it"
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5
                       rounded-lg bg-amber-500 px-4 py-2 text-[12.5px] font-semibold
                       text-white hover:bg-amber-600 transition-colors"
          >
            Got it — I'll start writing <ArrowRight className="h-3.5 w-3.5" />
          </button>

          {/* Secondary CTA — trigger File menu glow */}
          <button
            type="button"
            onClick={() => { onClose(); onShowLoadManuscript?.(); }}
            data-testid="onboarding-popup-show-load"
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5
                       rounded-lg border border-amber-300 px-4 py-2 text-[12px]
                       text-amber-700 hover:bg-amber-50 dark:border-amber-700/50
                       dark:text-amber-300 dark:hover:bg-amber-900/20 transition-colors"
          >
            <Folder className="h-3.5 w-3.5" />
            Show me how to load a manuscript
          </button>
        </div>
      </div>
    </>
  );
}
